import { PlusIcon } from '@heroicons/react/24/outline';
import { useQuery } from '@tanstack/react-query';
import type { ColumnDef } from '@tanstack/react-table';
import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { FilterBadgesList } from '@/components/FilterBadgesList';
import AccentTitle from '@/components/UI/AccentTitle';
import Badge from '@/components/UI/Badge';
import Button from '@/components/UI/Button';
import ErrorCard from '@/components/UI/ErrorCard';
import LoadingDeveloperApplications from '@/components/UI/loading/LoadingDeveloperApplications';
import { CurrencyFormatDisplay } from '@/components/UI/NumberFormatDisplay/CurrencyFormatDisplay';
import Table from '@/components/UI/Table';
import useEnvironment from '@/hooks/useEnvironment';
import { useFilters } from '@/hooks/useFilters';
import { getClientApplications } from '../../../api/Rover/clientApplication';
import { convertEnumToText, isObjectEmpty } from '../../../utils';
import { AddClientApplicationsFilterForm } from './AddClientApplicationsFilterForm';
import ClientApplicationsNav from './ClientApplicationsNav';

export default function ClientApplications() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isProd } = useEnvironment();

  const { showFilterModal, appliedFilters, filterBadges, toggleFilterModal, applyFiltersHandler, removeFilterHandler } = useFilters();

  const [params, setParams] = useState({
    page: 1,
    perPage: 10,
  });

  const fetchClientApplications = useQuery({
    queryKey: ['get-client-applications', { ...params, ...appliedFilters }],
    queryFn: () => getClientApplications({ ...params, ...appliedFilters }),
  });

  const clientApplications = fetchClientApplications.data?.data;
  const clientApplicationsCount = fetchClientApplications.data?.meta?.count;

  const totalPage = Math.ceil(Number(clientApplicationsCount) / params.perPage);

  const nextPageHandler = () => {
    if (params.page < totalPage) {
      setParams((prevState) => {
        return {
          ...prevState,
          page: prevState.page + 1,
        };
      });
    }
  };

  const previousPageHandler = () => {
    if (params.page > 1) {
      setParams((prevState) => {
        return {
          ...prevState,
          page: prevState.page - 1,
        };
      });
    }
  };

  const tablePagination = {
    page: params.page,
    pageSize: params.perPage,
    pageCount: totalPage,
    currentPage: params.page,
    setPageSize: (v: number) => setParams((prevState) => ({ ...prevState, perPage: v })),
    nextPage: nextPageHandler,
    previousPage: previousPageHandler,
  };

  const tableColumns: ColumnDef<any>[] = [
    {
      id: 'businessName',
      header: 'Business Name',
      accessorKey: 'orgName',
      size: 250,
      cell: (props) => {
        const { row, getValue } = props;
        return (
          <div className='text-sm text-neutralColor-medium flex flex-col max-w-md'>
            <span className='truncate'>{getValue() as string}</span>
            <div className='flex flex-col gap-1 text-xs text-neutralColor-normal'>
              <span>{row.original?.orgAddress}</span>
            </div>
          </div>
        );
      },
    },
    {
      id: 'sponsor',
      header: 'Sponsor',
      accessorKey: 'sponsor',
      size: 250,
      cell: (props) => {
        const { row, getValue } = props;
        return (
          <div className='text-sm text-neutralColor-medium flex flex-col max-w-md'>
            <span className='truncate'>{getValue() as string}</span>
            <div className='flex flex-col gap-1 text-xs text-neutralColor-normal'>
              <span>{row.original?.sponsorAddress}</span>
            </div>
          </div>
        );
      },
    },
    {
      id: 'amountRequested',
      header: () => <div className='text-center'>Amount Requested</div>,
      accessorKey: 'amountRequested',
      size: 200,
      cell: (props) => {
        const { getValue } = props;
        return (
          <div className='text-center'>
            <CurrencyFormatDisplay value={getValue() as number} prefix='$' />
          </div>
        );
      },
    },
    {
      id: 'socialDesignation',
      header: () => <div className='text-center'>Social Designation</div>,
      accessorKey: 'socialDesignation',
      size: 200,
      cell: (props) => {
        const { getValue } = props;
        return <div className='text-center w-full'>{getValue() as string}</div>;
      },
    },
    {
      id: 'status',
      header: () => <div className='text-center'>Status</div>,
      accessorKey: 'status',
      size: 250,
      cell: (props) => {
        const { getValue } = props;
        const statusValue = getValue() as string;
        return (
          <div className='text-center capitalize'>
            <Badge status={statusValue} text={convertEnumToText(statusValue)} />
          </div>
        );
      },
    },
  ];

  return (
    <>
      <div className='flex flex-col container mx-auto'>
        {!isProd && <ClientApplicationsNav />}
        <div className='border-neutralColor-light flex justify-between bg-white rounded-t-lg'>
          <AccentTitle title='Client Applications' />
        </div>

        <div className='flex flex-col bg-white gap-2 px-4 py-4 border-t'>
          <div className='flex flex-wrap gap-2 items-center'>
            <Button
              label='Add Filter'
              btnStyle='neutral'
              btnSize='md'
              icon={<PlusIcon className='h-5 w-5' />}
              onClick={toggleFilterModal}
            />
            {!isObjectEmpty(filterBadges) && <FilterBadgesList displayFilters={filterBadges} handleRemoveFilter={removeFilterHandler} />}
          </div>
          {!isObjectEmpty(filterBadges) && (
            <span className='m-0 text-neutralColor-normal mb-2'>{clientApplicationsCount} client applications found.</span>
          )}
        </div>
        {fetchClientApplications.isLoading && <LoadingDeveloperApplications />}
        {fetchClientApplications.isSuccess && clientApplicationsCount > 0 && (
          <Table
            cols={tableColumns}
            tableData={clientApplications}
            onRowClick={(row: any) => navigate(`${location.pathname}/${row.id}`)}
            pagination={tablePagination}
          />
        )}

        {!fetchClientApplications.isFetching && !!fetchClientApplications.error && (
          <div className='flex-grow flex justify-center items-center text-neutralColor-normal h-screen/2'>
            <ErrorCard
              errorTitle='Fetch Error'
              errorMsg='We are unable to pull the list of client applications from the database at this point. Please try again later. '
            />
          </div>
        )}
      </div>
      {showFilterModal && <AddClientApplicationsFilterForm handleModal={toggleFilterModal} handleApplyFilter={applyFiltersHandler} />}
    </>
  );
}
